// src/pages/TwoFASetup.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useApp } from '../AppContext';
import { Card, Btn, Flash } from '../components/UI';

export default function TwoFASetup() {
  const { user, refresh } = useApp();
  const navigate = useNavigate();
  const [step, setStep]       = useState('setup'); // setup | verify | backup
  const [secret, setSecret]   = useState('');
  const [qrB64, setQrB64]     = useState('');
  const [code, setCode]       = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    api.get('/api/2fa/setup').then(r => {
      if (r?.ok) { setSecret(r.data.secret); setQrB64(r.data.qr_b64); }
      setLoading(false);
    });
  }, []);

  async function verify() {
    if (!code.trim()) { setError('Enter the 6-digit code.'); return; }
    setVerifying(true); setError('');
    const r = await api.post('/api/2fa/verify', { secret, code });
    setVerifying(false);
    if (r?.ok) {
      setBackupCodes(r.data.backup_codes || []);
      setStep('backup');
      refresh();
    } else {
      setError(r?.data?.error || 'Invalid code.');
      setCode('');
    }
  }

  function downloadCodes() {
    const txt = ['SwimDesk 2FA Backup Codes', '========================', 'Each code can only be used once.', '', ...backupCodes].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([txt], { type: 'text/plain' }));
    a.download = '2fa-backup-codes.txt';
    a.click();
  }

  const inp = {
    width: '100%', padding: '10px 13px', border: '1px solid var(--input-border)',
    borderRadius: 'var(--radius)', background: 'var(--input-bg)', color: 'var(--fg)',
    fontSize: 22, fontFamily: 'var(--mono)', textAlign: 'center', letterSpacing: 10, outline: 'none',
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: 'var(--fg3)' }}>Loading...</div>;

  return (
    <div style={{ padding: '28px 24px', maxWidth: 520, margin: '0 auto', animation: 'fadeIn 0.2s ease' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg)', letterSpacing: '-0.02em', fontFamily: 'var(--font)', marginBottom: 4 }}>
          {step === 'backup' ? '2FA Enabled' : 'Set Up Two-Factor Authentication'}
        </h1>
        <p style={{ fontSize: 13, color: 'var(--fg3)' }}>
          {step === 'backup' ? 'Save your backup codes before continuing.' : 'Secure your account with an authenticator app.'}
        </p>
      </div>

      {step === 'setup' && (
        <Card>
          {/* Step 1 */}
          <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>1</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, fontFamily: 'var(--font)' }}>Install an authenticator app</div>
              <p style={{ fontSize: 13, color: 'var(--fg3)' }}>Download Google Authenticator, Authy, or any TOTP-compatible app on your phone.</p>
            </div>
          </div>

          {/* Step 2 */}
          <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>2</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, fontFamily: 'var(--font)' }}>Scan the QR code</div>
              <p style={{ fontSize: 13, color: 'var(--fg3)', marginBottom: 14 }}>Open your authenticator app and scan the code below.</p>
              <div style={{ background: '#fff', padding: 12, display: 'inline-block', borderRadius: 'var(--radius)', border: '1px solid var(--border)', marginBottom: 10 }}>
                {qrB64 && <img src={`data:image/png;base64,${qrB64}`} alt="2FA QR Code" style={{ width: 160, height: 160, display: 'block' }} />}
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--fg2)', letterSpacing: 2, wordBreak: 'break-all' }}>{secret}</div>
              <p style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 4 }}>Manual entry key — use if you can't scan the QR code</p>
            </div>
          </div>

          {/* Step 3 */}
          <div style={{ padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>3</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, fontFamily: 'var(--font)' }}>Enter the 6-digit code</div>
              <p style={{ fontSize: 13, color: 'var(--fg3)', marginBottom: 12 }}>Enter the code from your app to confirm setup.</p>
              {error && <div style={{ padding: '8px 12px', background: 'var(--danger-dim)', border: '1px solid var(--danger)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
              <input style={inp} type="text" inputMode="numeric" maxLength={6} value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                onKeyDown={e => e.key === 'Enter' && verify()}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--input-border)'}
                placeholder="000000" autoFocus />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Btn onClick={verify} disabled={verifying}>{verifying ? 'Verifying...' : 'Verify & Enable 2FA'}</Btn>
                <Btn variant="ghost" onClick={() => navigate('/account')}>Cancel</Btn>
              </div>
            </div>
          </div>
        </Card>
      )}

      {step === 'backup' && (
        <Card>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', background: 'var(--success-dim)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>2FA is now enabled on your account.</div>
          </div>
          <div style={{ padding: '16px 18px' }}>
            <div style={{ padding: '10px 14px', background: 'var(--warning-dim)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)', fontSize: 13, color: 'var(--warning)', marginBottom: 16, fontWeight: 500 }}>
              Save these codes now. Each can only be used once. Store them somewhere safe.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
              {backupCodes.map((c, i) => (
                <div key={i} style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 500, textAlign: 'center', padding: '10px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--fg)', letterSpacing: 2 }}>{c}</div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn onClick={downloadCodes}>Download Codes</Btn>
              <Btn variant="ghost" onClick={() => navigate('/account')}>Continue to Account</Btn>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
